import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Buildings, IdentificationCard, Bank, Warehouse, Briefcase, TreeStructure,
  FileText, ShieldCheck, ArrowRight, ArrowLeft, CheckCircle, Plus, Trash, Check
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'business_details', title: 'Business Details', icon: Buildings, desc: 'Legal name, PAN, GSTIN' },
  { key: 'brand_details', title: 'Brand Details', icon: IdentificationCard, desc: 'Brands & authorization' },
  { key: 'bank_details', title: 'Bank Details', icon: Bank, desc: 'Account & IFSC verification' },
  { key: 'warehouse_details', title: 'Warehouse', icon: Warehouse, desc: 'Fulfillment locations' },
  { key: 'business_model', title: 'Business Model', icon: Briefcase, desc: 'Operating model selection' },
  { key: 'category_selection', title: 'Categories', icon: TreeStructure, desc: 'Product categories' },
  { key: 'documents', title: 'Documents', icon: FileText, desc: 'KYC & compliance docs' },
  { key: 'declaration', title: 'Declaration', icon: ShieldCheck, desc: 'Terms & submission' },
];

const BIZ_TYPES = ['Individual','Sole Proprietorship','Partnership','LLP','Private Limited','Public Limited','OPC','HUF','Trust'];
const BIZ_MODELS = ['Inventory-led','Marketplace','Dropship','Omni'];
const DOC_TYPES = ['PAN Card','GST Certificate','Address Proof','Bank Statement','Brand Authorization','NOC','Indemnity','Cancelled Cheque','Other'];

export default function SellerOnboardingWizardPage() {
  const [sellerId, setSellerId] = useState(null);
  const [step, setStep] = useState(0);
  const [stepsStatus, setStepsStatus] = useState({});
  const [pct, setPct] = useState(0);
  const [appStatus, setAppStatus] = useState('draft');
  const [saving, setSaving] = useState(false);

  // Step data
  const [biz, setBiz] = useState({ legal_name: '', business_type: 'Individual', pan: '', gstin: '', trade_name: '', registered_address: '' });
  const [brands, setBrands] = useState([]);
  const [brandForm, setBrandForm] = useState({ brand_name: '', ownership_type: 'Owned', authorization_doc_url: '' });
  const [bank, setBank] = useState({ account_number: '', ifsc_code: '', account_holder_name: '', bank_name: '', branch: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [whForm, setWhForm] = useState({ warehouse_name: '', address: '', city: '', state: '', pincode: '', contact_phone: '' });
  const [bizModels, setBizModels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docForm, setDocForm] = useState({ doc_type: 'PAN Card', doc_name: '', doc_url: '', tag: 'KYC' });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Check if user has an existing onboarding application
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/onboarding/applications?limit=100`, { withCredentials: true });
        // Find current user's application (simplified — in prod, use session)
        if (data.applications?.length > 0) {
          const app = data.applications[0]; // Most recent
          await loadApplication(app.seller_id);
        }
      } catch { /* no existing */ }
      // Load categories
      try {
        const { data } = await axios.get(`${API}/api/categories`, { withCredentials: true });
        setAllCats(data || []);
      } catch {}
    };
    loadExisting();
  }, []);

  const loadApplication = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding/${sid}`, { withCredentials: true });
      setSellerId(sid);
      setStepsStatus(data.steps_status || {});
      setPct(data.completion_percent || 0);
      setAppStatus(data.status);
      // Populate forms from saved data
      setBiz({ legal_name: data.legal_name || '', business_type: data.business_type || 'Individual', pan: data.kyc?.pan || '', gstin: data.kyc?.gstin || '', trade_name: data.trade_name || '', registered_address: data.registered_address || '' });
      if (data.bank) setBank({ account_number: data.bank.account_number || '', ifsc_code: data.bank.ifsc_code || '', account_holder_name: data.bank.account_holder_name || '', bank_name: data.bank.bank_name || '', branch: data.bank.branch || '' });
      if (data.brands) setBrands(data.brands);
      if (data.warehouses) setWarehouses(data.warehouses);
      if (data.business_models) setBizModels(data.business_models);
      if (data.selected_categories) setCategories(data.selected_categories);
      if (data.documents) setDocuments(data.documents);
      // Jump to first incomplete step
      const firstIncomplete = STEPS.findIndex(s => !(data.steps_status || {})[s.key]);
      if (firstIncomplete >= 0) setStep(firstIncomplete);
    } catch {}
  };

  const createApplication = async () => {
    try {
      const { data } = await axios.post(`${API}/api/onboarding/register`, {
        legal_name: 'New Seller', email: `seller_${Date.now()}@test.com`, phone: `9${Date.now().toString().slice(-9)}`, password: 'test123', business_type: 'Individual'
      });
      setSellerId(data.seller_id);
      return data.seller_id;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); return null; }
  };

  const ensureSellerId = async () => {
    if (sellerId) return sellerId;
    return await createApplication();
  };

  // Step savers
  const saveBizDetails = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (!biz.legal_name || !biz.pan) { toast.error('Legal name and PAN required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/onboarding/${sid}/business-details`, biz, { withCredentials: true });
      toast.success('Business details saved');
      setStepsStatus(s => ({ ...s, business_details: true }));
      setPct(data.completion_percent);
      setStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const addBrand = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (!brandForm.brand_name) { toast.error('Brand name required'); return; }
    try {
      const { data } = await axios.post(`${API}/api/onboarding/${sid}/brands`, brandForm, { withCredentials: true });
      setBrands(prev => [...prev, data]);
      setBrandForm({ brand_name: '', ownership_type: 'Owned', authorization_doc_url: '' });
      setStepsStatus(s => ({ ...s, brand_details: true }));
      toast.success('Brand added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeBrand = async (bid) => {
    try {
      await axios.delete(`${API}/api/onboarding/${sellerId}/brands/${bid}`, { withCredentials: true });
      setBrands(prev => prev.filter(b => b.brand_id !== bid));
    } catch { toast.error('Failed to remove'); }
  };

  const saveBankDetails = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (!bank.account_number || !bank.ifsc_code || !bank.account_holder_name) { toast.error('All bank fields required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/onboarding/${sid}/bank-details`, bank, { withCredentials: true });
      toast.success('Bank verified');
      setStepsStatus(s => ({ ...s, bank_details: true }));
      setPct(data.completion_percent);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const addWarehouse = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (!whForm.warehouse_name || !whForm.pincode) { toast.error('Name and pincode required'); return; }
    try {
      const { data } = await axios.post(`${API}/api/onboarding/${sid}/warehouses`, whForm, { withCredentials: true });
      setWarehouses(prev => [...prev, data]);
      setWhForm({ warehouse_name: '', address: '', city: '', state: '', pincode: '', contact_phone: '' });
      setStepsStatus(s => ({ ...s, warehouse_details: true }));
      toast.success('Warehouse added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveBizModel = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (bizModels.length === 0) { toast.error('Select at least one'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/onboarding/${sid}/business-model`, { business_models: bizModels }, { withCredentials: true });
      setStepsStatus(s => ({ ...s, business_model: true }));
      setPct(data.completion_percent);
      setStep(5);
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveCategories = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (categories.length === 0) { toast.error('Select at least one'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/onboarding/${sid}/categories`, { categories }, { withCredentials: true });
      setStepsStatus(s => ({ ...s, category_selection: true }));
      setPct(data.completion_percent);
      setStep(6);
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const addDocument = async () => {
    const sid = await ensureSellerId(); if (!sid) return;
    if (!docForm.doc_url) { toast.error('Document URL required'); return; }
    try {
      const { data } = await axios.post(`${API}/api/onboarding/${sid}/documents`, docForm, { withCredentials: true });
      setDocuments(prev => [...prev, data]);
      setDocForm({ doc_type: 'PAN Card', doc_name: '', doc_url: '', tag: 'KYC' });
      setStepsStatus(s => ({ ...s, documents: true }));
      toast.success('Document uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitApplication = async () => {
    if (!termsAccepted) { toast.error('Accept terms first'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/onboarding/${sellerId}/submit`, { terms_accepted: true }, { withCredentials: true });
      setAppStatus('submitted');
      setStepsStatus(s => ({ ...s, declaration: true }));
      setPct(100);
      toast.success('Application submitted for review!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSaving(false); }
  };

  const isSubmitted = ['submitted','under_review','approved','activated'].includes(appStatus);
  const completedCount = Object.values(stepsStatus).filter(Boolean).length;

  const StepContent = () => {
    switch(step) {
      case 0: return (
        <div className="space-y-4" data-testid="step-business-details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Legal Name *</Label><Input value={biz.legal_name} onChange={e => setBiz(b => ({ ...b, legal_name: e.target.value }))} placeholder="Company legal name" data-testid="onb-legal-name" /></div>
            <div><Label>Business Type *</Label>
              <Select value={biz.business_type} onValueChange={v => setBiz(b => ({ ...b, business_type: v }))}><SelectTrigger data-testid="onb-biz-type"><SelectValue /></SelectTrigger><SelectContent>{BIZ_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>PAN *</Label><Input value={biz.pan} onChange={e => setBiz(b => ({ ...b, pan: e.target.value.toUpperCase() }))} placeholder="ABCDE1234F" maxLength={10} className="font-mono" data-testid="onb-pan" /></div>
            <div><Label>GSTIN</Label><Input value={biz.gstin} onChange={e => setBiz(b => ({ ...b, gstin: e.target.value.toUpperCase() }))} placeholder="22AAAAA0000A1Z5" maxLength={15} className="font-mono" data-testid="onb-gstin" /></div>
          </div>
          <div><Label>Trade Name</Label><Input value={biz.trade_name} onChange={e => setBiz(b => ({ ...b, trade_name: e.target.value }))} placeholder="DBA name" /></div>
          <div><Label>Registered Address</Label><Textarea value={biz.registered_address} onChange={e => setBiz(b => ({ ...b, registered_address: e.target.value }))} rows={2} placeholder="Full address" /></div>
          <Button onClick={saveBizDetails} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="save-biz-btn">{saving ? 'Validating...' : 'Save & Validate'}</Button>
        </div>
      );
      case 1: return (
        <div className="space-y-4" data-testid="step-brand-details">
          {brands.length > 0 && <div className="space-y-2">{brands.map(b => (
            <div key={b.brand_id} className="flex items-center justify-between bg-[#f9f9fb] rounded-lg px-4 py-2.5">
              <div><span className="font-medium text-sm">{b.brand_name}</span><span className="text-xs text-[#9c9db7] ml-2">({b.ownership_type})</span></div>
              <Button variant="ghost" size="sm" className="text-red-400" onClick={() => removeBrand(b.brand_id)}><Trash size={14} /></Button>
            </div>
          ))}</div>}
          <div className="bg-[#f9f9fb] rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand Name</Label><Input value={brandForm.brand_name} onChange={e => setBrandForm(f => ({ ...f, brand_name: e.target.value }))} placeholder="Your brand" data-testid="onb-brand-name" /></div>
              <div><Label>Ownership</Label>
                <Select value={brandForm.ownership_type} onValueChange={v => setBrandForm(f => ({ ...f, ownership_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Owned">Owned</SelectItem><SelectItem value="Authorized">Authorized</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div><Label>Authorization Doc URL</Label><Input value={brandForm.authorization_doc_url} onChange={e => setBrandForm(f => ({ ...f, authorization_doc_url: e.target.value }))} placeholder="https://..." /></div>
            <Button onClick={addBrand} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-sm" data-testid="add-brand-btn"><Plus size={14} className="mr-1" /> Add Brand</Button>
          </div>
          {brands.length > 0 && <Button onClick={() => setStep(2)} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white">Continue <ArrowRight size={14} className="ml-1" /></Button>}
        </div>
      );
      case 2: return (
        <div className="space-y-4" data-testid="step-bank-details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Account Number *</Label><Input value={bank.account_number} onChange={e => setBank(b => ({ ...b, account_number: e.target.value }))} placeholder="Account number" className="font-mono" data-testid="onb-account" /></div>
            <div><Label>IFSC Code *</Label><Input value={bank.ifsc_code} onChange={e => setBank(b => ({ ...b, ifsc_code: e.target.value.toUpperCase() }))} placeholder="SBIN0001234" maxLength={11} className="font-mono" data-testid="onb-ifsc" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Account Holder Name *</Label><Input value={bank.account_holder_name} onChange={e => setBank(b => ({ ...b, account_holder_name: e.target.value }))} data-testid="onb-holder" /></div>
            <div><Label>Bank Name</Label><Input value={bank.bank_name} onChange={e => setBank(b => ({ ...b, bank_name: e.target.value }))} /></div>
          </div>
          <Button onClick={saveBankDetails} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="save-bank-btn">{saving ? 'Verifying...' : 'Verify & Save'}</Button>
        </div>
      );
      case 3: return (
        <div className="space-y-4" data-testid="step-warehouse">
          {warehouses.length > 0 && <div className="space-y-2">{warehouses.map(w => (
            <div key={w.warehouse_id} className="bg-[#f9f9fb] rounded-lg px-4 py-2.5 text-sm"><span className="font-medium">{w.warehouse_name}</span> — <span className="text-[#9c9db7]">{w.city}, {w.pincode}</span></div>
          ))}</div>}
          <div className="bg-[#f9f9fb] rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Warehouse Name *</Label><Input value={whForm.warehouse_name} onChange={e => setWhForm(f => ({ ...f, warehouse_name: e.target.value }))} data-testid="onb-wh-name" /></div>
              <div><Label>Pincode *</Label><Input value={whForm.pincode} onChange={e => setWhForm(f => ({ ...f, pincode: e.target.value }))} data-testid="onb-wh-pincode" /></div>
            </div>
            <div><Label>Address</Label><Input value={whForm.address} onChange={e => setWhForm(f => ({ ...f, address: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input value={whForm.city} onChange={e => setWhForm(f => ({ ...f, city: e.target.value }))} /></div>
              <div><Label>State</Label><Input value={whForm.state} onChange={e => setWhForm(f => ({ ...f, state: e.target.value }))} /></div>
            </div>
            <Button onClick={addWarehouse} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-sm" data-testid="add-wh-btn"><Plus size={14} className="mr-1" /> Add Warehouse</Button>
          </div>
          {warehouses.length > 0 && <Button onClick={() => setStep(4)} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white">Continue <ArrowRight size={14} className="ml-1" /></Button>}
        </div>
      );
      case 4: return (
        <div className="space-y-4" data-testid="step-biz-model">
          <p className="text-sm text-[#6b6b8a]">Select your operating model(s):</p>
          <div className="grid grid-cols-2 gap-3">{BIZ_MODELS.map(m => (
            <button key={m} type="button" onClick={() => setBizModels(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])} className={`px-4 py-3 rounded-xl border text-left transition-all text-sm ${bizModels.includes(m) ? 'border-[#7d64ff] bg-[#7d64ff]/5 font-semibold text-[#7d64ff]' : 'border-[#e5e5ea] hover:border-[#7d64ff]/30 text-[#3c3c64]'}`} data-testid={`biz-model-${m}`}>
              <div className="flex items-center gap-2">{bizModels.includes(m) && <CheckCircle size={16} weight="fill" />}{m}</div>
            </button>
          ))}</div>
          <Button onClick={saveBizModel} disabled={saving || bizModels.length === 0} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="save-biz-model-btn">{saving ? 'Saving...' : 'Save & Continue'}</Button>
        </div>
      );
      case 5: return (
        <div className="space-y-4" data-testid="step-categories">
          <p className="text-sm text-[#6b6b8a]">Select product categories you'll sell in:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">{allCats.filter((c, i, a) => a.findIndex(x => x.name === c.name) === i).map(c => (
            <button key={c.category_id} type="button" onClick={() => setCategories(prev => prev.includes(c.name) ? prev.filter(x => x !== c.name) : [...prev, c.name])} className={`px-3 py-2 rounded-lg border text-xs text-left transition-all ${categories.includes(c.name) ? 'border-[#7d64ff] bg-[#7d64ff]/5 font-semibold text-[#7d64ff]' : 'border-[#e5e5ea] text-[#3c3c64]'}`}>
              {categories.includes(c.name) && <Check size={10} className="inline mr-1" />}{c.name}
            </button>
          ))}</div>
          <Button onClick={saveCategories} disabled={saving || categories.length === 0} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white" data-testid="save-cats-btn">{saving ? 'Saving...' : 'Save & Continue'}</Button>
        </div>
      );
      case 6: return (
        <div className="space-y-4" data-testid="step-documents">
          {documents.length > 0 && <div className="space-y-2">{documents.map(d => (
            <div key={d.doc_id} className="flex items-center justify-between bg-[#f9f9fb] rounded-lg px-4 py-2.5 text-sm">
              <div><span className="font-medium">{d.doc_name || d.doc_type}</span><span className="ml-2 px-1.5 py-0.5 bg-gray-200 rounded text-[10px]">{d.tag}</span></div>
              <a href={d.doc_url} target="_blank" rel="noreferrer" className="text-xs text-[#7d64ff] hover:underline">View</a>
            </div>
          ))}</div>}
          <div className="bg-[#f9f9fb] rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Document Type</Label>
                <Select value={docForm.doc_type} onValueChange={v => setDocForm(f => ({ ...f, doc_type: v }))}><SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Tag</Label>
                <Select value={docForm.tag} onValueChange={v => setDocForm(f => ({ ...f, tag: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['KYC','Bank','Brand','Legal'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label>Document Name</Label><Input value={docForm.doc_name} onChange={e => setDocForm(f => ({ ...f, doc_name: e.target.value }))} placeholder="e.g. PAN Card Copy" /></div>
            <div><Label>Document URL *</Label><Input value={docForm.doc_url} onChange={e => setDocForm(f => ({ ...f, doc_url: e.target.value }))} placeholder="https://..." data-testid="doc-url" /></div>
            <Button onClick={addDocument} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white text-sm" data-testid="add-doc-btn"><Plus size={14} className="mr-1" /> Upload Document</Button>
          </div>
          {documents.length > 0 && <Button onClick={() => setStep(7)} className="bg-[#7d64ff] hover:bg-[#6b4fef] text-white">Continue <ArrowRight size={14} className="ml-1" /></Button>}
        </div>
      );
      case 7: return (
        <div className="space-y-5" data-testid="step-declaration">
          <div className="bg-[#f9f9fb] rounded-xl p-5 text-sm text-[#3c3c64] space-y-2">
            <p className="font-semibold">Terms & Conditions</p>
            <p>By submitting this application, I declare that:</p>
            <ul className="list-disc list-inside space-y-1 text-[#6b6b8a]">
              <li>All information provided is true and accurate</li>
              <li>I am authorized to represent this business</li>
              <li>I agree to the marketplace seller terms of service</li>
              <li>I consent to KYC and compliance verification</li>
              <li>I understand that false information may lead to account termination</li>
            </ul>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="w-5 h-5 rounded accent-[#7d64ff]" data-testid="accept-terms" />
            <span className="text-sm font-medium">I accept the terms and conditions</span>
          </label>
          <Button onClick={submitApplication} disabled={saving || !termsAccepted} className="w-full bg-green-600 hover:bg-green-700 text-white py-3" data-testid="submit-application-btn">
            <ShieldCheck size={18} className="mr-2" /> {saving ? 'Submitting...' : 'Submit Application for Review'}
          </Button>
        </div>
      );
      default: return null;
    }
  };

  if (isSubmitted) return (
    <DashboardLayout>
      <div className="p-6 max-w-2xl mx-auto text-center py-16" data-testid="onboarding-submitted">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"><CheckCircle size={40} className="text-green-600" /></div>
        <h2 className="text-2xl font-bold text-[#0A0A0A] mb-2">Application {appStatus === 'activated' ? 'Activated' : appStatus === 'approved' ? 'Approved' : 'Submitted'}!</h2>
        <p className="text-[#6b6b8a]">{appStatus === 'activated' ? 'Your seller account is live. You can now upload catalog and process orders.' : appStatus === 'approved' ? 'Your application has been approved. Awaiting activation.' : 'Your application is under review. We will notify you once it\'s processed.'}</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="onboarding-wizard-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Seller Onboarding</h1>
          <p className="text-sm text-[#6b6b8a] mt-1">Complete all steps to submit your seller application</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-2.5 bg-[#e5e5ea] rounded-full overflow-hidden"><div className="h-full bg-[#7d64ff] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          <span className="text-sm font-bold text-[#7d64ff]">{pct}%</span>
        </div>

        {/* Step indicators */}
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const done = stepsStatus[s.key];
            const active = i === step;
            return (
              <button key={s.key} onClick={() => setStep(i)} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs whitespace-nowrap transition-all ${active ? 'border-[#7d64ff] bg-[#7d64ff]/5 text-[#7d64ff] font-semibold' : done ? 'border-green-300 bg-green-50 text-green-700' : 'border-[#e5e5ea] text-[#6b6b8a] hover:border-[#7d64ff]/30'}`} data-testid={`step-nav-${s.key}`}>
                {done ? <CheckCircle size={14} weight="fill" className="text-green-600" /> : <s.icon size={14} />}
                <span className="hidden md:inline">{s.title}</span>
                <span className="md:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Step content */}
        <div className="bg-white border border-[#e5e5ea] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-5">
            {(() => { const S = STEPS[step]; return <><div className="w-10 h-10 rounded-xl bg-[#7d64ff]/10 flex items-center justify-center"><S.icon size={22} className="text-[#7d64ff]" /></div><div><h2 className="text-lg font-bold text-[#0A0A0A]">{S.title}</h2><p className="text-xs text-[#9c9db7]">Step {step + 1} of {STEPS.length} — {S.desc}</p></div></>; })()}
          </div>
          <StepContent />
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-4">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)}><ArrowLeft size={14} className="mr-1" /> Previous</Button>
          {step < STEPS.length - 1 && <Button variant="ghost" onClick={() => setStep(s => s + 1)}>Skip <ArrowRight size={14} className="ml-1" /></Button>}
        </div>
      </div>
    </DashboardLayout>
  );
}
